import React, { useMemo } from 'react';
import { Box, CircularProgress, Grid, Link as MuiLink, Paper, Typography } from '@mui/material';
import { packEnclose, packSiblings } from 'd3-hierarchy';
import { IndustryCompany, IndustryPanel } from '../../types/industry';
import { SizeBucketId, SIZE_BUCKETS_BY_ID, UNKNOWN_SIZE_BUCKET } from '../../utils/sizeBuckets';

type BubbleDatum = {
  id: string;
  companyName: string;
  sizeLabel: string;
  sizeValue: number;
  sizePriority: number;
  latestReportingYear: number | null;
  yearsReported: number | null;
  compositeScore: number | null;
};

type PackedBubble = BubbleDatum & {
  x: number;
  y: number;
  r: number;
};

const CHART_SIZE = 260;
const CHART_CENTER = CHART_SIZE / 2;
const MAX_COMPANIES_PER_PANEL = 250;
const RECENT_REPORTING_WINDOW_YEARS = 1;
const MIN_YEARS_REPORTED = 3;

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseIntInRange = (value: unknown, min: number, max: number): number | null => {
  const parsed = parseNumber(value);
  if (parsed === null) return null;
  const rounded = Math.round(parsed);
  return rounded >= min && rounded <= max ? rounded : null;
};

const toBubbleDatum = (company: IndustryCompany, index: number): BubbleDatum | null => {
  const sizeBucketId =
    typeof company.EmployerSizeBucket === 'string' ? (company.EmployerSizeBucket.trim() as SizeBucketId) : '';
  if (!sizeBucketId) {
    return null;
  }

  const sizeBucket = SIZE_BUCKETS_BY_ID.get(sizeBucketId) ?? UNKNOWN_SIZE_BUCKET;
  const sizeLabel = String(company.EmployerSize || '').trim();

  return {
    id: String(company.id ?? `company-${index}`),
    companyName: String(company.CleanCompanyName || 'Company'),
    sizeLabel: sizeLabel || sizeBucket.label,
    sizeValue: sizeBucket.radiusMultiplier * sizeBucket.radiusMultiplier,
    sizePriority: sizeBucket.priority,
    latestReportingYear: parseIntInRange(company.LatestReportingYear, 1990, 2100),
    yearsReported: parseIntInRange(company.YearsReported, 0, 100),
    compositeScore: parseNumber(company.FinalCompositeScore),
  };
};

const buildRecentYearCutoff = (bubbles: BubbleDatum[]): number | null => {
  const maxYear = bubbles.reduce<number | null>((current, bubble) => {
    if (bubble.latestReportingYear === null) return current;
    if (current === null || bubble.latestReportingYear > current) return bubble.latestReportingYear;
    return current;
  }, null);

  return maxYear === null ? null : maxYear - RECENT_REPORTING_WINDOW_YEARS;
};

const reportingPriority = (bubble: BubbleDatum, recentYearCutoff: number | null): number => {
  const isRecent =
    recentYearCutoff === null
      ? bubble.latestReportingYear !== null
      : bubble.latestReportingYear !== null && bubble.latestReportingYear >= recentYearCutoff;
  const isMultiYear = (bubble.yearsReported ?? 0) >= MIN_YEARS_REPORTED;
  if (isRecent && isMultiYear) return 3;
  if (isRecent) return 2;
  if (isMultiYear) return 1;
  return 0;
};

const selectTopBubbles = (bubbleData: BubbleDatum[]): BubbleDatum[] => {
  const recentYearCutoff = buildRecentYearCutoff(bubbleData);

  return [...bubbleData]
    .sort((a, b) => {
      const sizePriority = b.sizePriority - a.sizePriority;
      if (sizePriority !== 0) return sizePriority;

      const reporting = reportingPriority(b, recentYearCutoff) - reportingPriority(a, recentYearCutoff);
      if (reporting !== 0) return reporting;

      const yearDelta = (b.latestReportingYear ?? -Infinity) - (a.latestReportingYear ?? -Infinity);
      if (yearDelta !== 0) return yearDelta;

      const yearsDelta = (b.yearsReported ?? -Infinity) - (a.yearsReported ?? -Infinity);
      if (yearsDelta !== 0) return yearsDelta;

      const scoreDelta = (b.compositeScore ?? -Infinity) - (a.compositeScore ?? -Infinity);
      if (scoreDelta !== 0) return scoreDelta;

      return a.companyName.localeCompare(b.companyName);
    })
    .slice(0, MAX_COMPANIES_PER_PANEL);
};

const packBubbles = (bubbles: BubbleDatum[]): { bubbles: PackedBubble[]; clusterRadius: number } => {
  if (bubbles.length === 0) {
    return { bubbles: [], clusterRadius: 0 };
  }

  const packed: PackedBubble[] = bubbles.map((bubble) => ({
    ...bubble,
    x: 0,
    y: 0,
    r: Math.sqrt(Math.max(0, bubble.sizeValue)),
  }));

  packSiblings(packed as any);
  const enclosing = packEnclose(packed as any);

  return {
    bubbles: packed.map((bubble) => ({
      ...bubble,
      x: CHART_CENTER + (bubble.x - enclosing.x),
      y: CHART_CENTER + (bubble.y - enclosing.y),
    })),
    clusterRadius: enclosing.r,
  };
};

const scaleBubbles = (bubbles: PackedBubble[], scaleFactor: number): PackedBubble[] => {
  if (!Number.isFinite(scaleFactor) || scaleFactor <= 0 || scaleFactor === 1) {
    return bubbles;
  }

  return bubbles.map((bubble) => ({
    ...bubble,
    x: CHART_CENTER + (bubble.x - CHART_CENTER) * scaleFactor,
    y: CHART_CENTER + (bubble.y - CHART_CENTER) * scaleFactor,
    r: bubble.r * scaleFactor,
  }));
};

const IndustryPanelPlot: React.FC<{
  panel: IndustryPanel;
  bubbles: PackedBubble[];
  onIndustrySelect?: (industryCode: number) => void;
}> = ({ panel, bubbles, onIndustrySelect }) => (
  <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
      <Box>
        {onIndustrySelect ? (
          <MuiLink
            component="button"
            type="button"
            underline="hover"
            color="inherit"
            onClick={() => onIndustrySelect(panel.industry_code)}
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: 1.2,
              textAlign: 'left',
              display: 'inline-block',
              background: 'none',
              border: 0,
              padding: 0,
              cursor: 'pointer',
            }}
          >
            {panel.name}
          </MuiLink>
        ) : (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {panel.name}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" component="div" sx={{ display: 'block' }}>
          {panel.count.toLocaleString()} companies
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
        {Number.isFinite(panel.avgScore) ? panel.avgScore.toFixed(1) : '0.0'}
      </Typography>
    </Box>

    <Box sx={{ width: '100%', aspectRatio: '1 / 1', mt: 1 }}>
      <svg viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`} width="100%" height="100%" role="img" aria-label={`${panel.name} bubble chart`}>
        {bubbles.map((bubble, index) => (
          <circle
            key={`${bubble.id}-${index}`}
            cx={bubble.x}
            cy={bubble.y}
            r={bubble.r}
            fill="#5f6ecf"
            fillOpacity={0.65}
            stroke="#ffffff"
            strokeWidth={0.7}
          >
            <title>{`${bubble.companyName} (${bubble.sizeLabel})`}</title>
          </circle>
        ))}
      </svg>
    </Box>
  </Paper>
);

const IndustryCirclePackingGrid: React.FC<{
  panels: IndustryPanel[];
  loading: boolean;
  onIndustrySelect?: (industryCode: number) => void;
}> = ({ panels, loading, onIndustrySelect }) => {
  const panelLayouts = useMemo(() => {
    const initialLayouts = panels.map((panel) => {
      const bubbleData = (panel.companies || [])
        .map((company, index) => toBubbleDatum(company, index))
        .filter((bubble): bubble is BubbleDatum => bubble !== null);

      const selected = selectTopBubbles(bubbleData);
      const packed = packBubbles(selected);

      return {
        panel,
        bubbles: packed.bubbles,
        clusterRadius: packed.clusterRadius,
      };
    });

    const maxClusterRadius = initialLayouts.reduce((maxRadius, layout) => {
      if (!Number.isFinite(layout.clusterRadius) || layout.clusterRadius <= 0) return maxRadius;
      return Math.max(maxRadius, layout.clusterRadius);
    }, 0);

    const targetRadius = CHART_CENTER - 2;
    const globalScale = maxClusterRadius > 0 ? targetRadius / maxClusterRadius : 1;

    return initialLayouts.map((layout) => ({
      panel: layout.panel,
      bubbles: scaleBubbles(layout.bubbles, globalScale),
    }));
  }, [panels]);

  if (loading) {
    return (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!panelLayouts.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No industry data available for this view.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {panelLayouts.map(({ panel, bubbles }) => (
        <Grid item xs={12} sm={6} md={4} key={panel.industry_code}>
          <IndustryPanelPlot panel={panel} bubbles={bubbles} onIndustrySelect={onIndustrySelect} />
        </Grid>
      ))}
    </Grid>
  );
};

export default IndustryCirclePackingGrid;
